function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import * as Sentry from "@sentry/nextjs";
import { inputTypes } from "client/services/form-service/form-inputs";
import { trackEvent } from "services/bigbrain-service";
import { INPUT_TYPE_TO_HUBSPOT_FIELD_TYPE_MAP } from "../components/contact-sales-hubspot-form-component-consts";
/**
 * Helper function to parse a single HubSpot field into BaseForm format
 */ export const parseHubspotField = (field)=>{
    var _field_options;
    if (!field || field.hidden) {
        return null;
    }
    const fieldType = INPUT_TYPE_TO_HUBSPOT_FIELD_TYPE_MAP[field.fieldType] || inputTypes.TEXT;
    const fieldLabel = field.label || field.name;
    const isRequired = field.required || false;
    const placeholderWithAsterisk = isRequired ? `${fieldLabel}*` : fieldLabel;
    const inputData = {
        id: field.name,
        inputName: field.name,
        type: fieldType,
        label: fieldLabel,
        placeholder: placeholderWithAsterisk,
        isRequired,
        defaultValue: field.defaultValue || ""
    };
    if (!((_field_options = field.options) === null || _field_options === void 0 ? void 0 : _field_options.length)) {
        return inputData;
    }
    inputData.options = field.options.map((options)=>({
            label: options.label,
            value: options.value
        }));
    inputData.isMulti = false;
    inputData.closeMenuOnSelect = true;
    return inputData;
};
/**
 * Helper function to check if form is in English
 */ export const isFormInEnglish = (hubspotData)=>{
    var _hubspotData_form_metaData, _hubspotData_form;
    // Check metadata for language
    const langMetadata = (_hubspotData_form = hubspotData.form) === null || _hubspotData_form === void 0 ? void 0 : (_hubspotData_form_metaData = _hubspotData_form.metaData) === null || _hubspotData_form_metaData === void 0 ? void 0 : _hubspotData_form_metaData.find((item)=>item.name === 'lang');
    if (langMetadata === null || langMetadata === void 0 ? void 0 : langMetadata.value) {
        return langMetadata.value.toLowerCase().startsWith('en');
    }
    return false; // Default to non-English if can't determine
};
/**
 * Helper function to track fallback events
 */ export const trackFallbackEvent = (formId, reason, data = {})=>{
    trackEvent('HUBSPOT_FORM_FALLBACK', {
        kind: formId,
        info1: reason,
        data: _object_spread_props(_object_spread({}, data), {
            formId
        })
    });
};
/**
 * Check field-specific fallback conditions
 */ export const checkFieldFallbacks = (field, formId)=>{
    if (field.hidden) {
        return null;
    }
    // Unknown field type
    if (!INPUT_TYPE_TO_HUBSPOT_FIELD_TYPE_MAP[field.fieldType]) {
        trackFallbackEvent(formId, "unknown_field_type", {
            fieldType: field.fieldType,
            fieldName: field.name
        });
        return "unknown_field_type";
    }
    // Missing field label
    if (!field.label) {
        trackFallbackEvent(formId, "missing_field_label", {
            fieldName: field.name
        });
        return "missing_field_label";
    }
    // Field label too long
    if (field.label.length > 50) {
        trackFallbackEvent(formId, "field_label_too_long", {
            fieldName: field.name,
            labelLength: field.label.length,
            label: field.label.substring(0, 50) + "..."
        });
        return "field_label_too_long";
    }
    return null;
};
/**
 * Check all fallback conditions
 */ export const shouldUseFallback = (hubspotData, localeId, formId)=>{
    // Locale mismatch check
    if (localeId && isFormInEnglish(hubspotData)) {
        trackFallbackEvent(formId, "locale_exists_but_form_in_english", {
            localeId
        });
        return true;
    }
    // Field-specific checks
    const allFields = hubspotData.form.formFieldGroups.flatMap((group)=>group.fields);
    return allFields.some((field)=>checkFieldFallbacks(field, formId));
};
/**
 * Extract privacy policy text from HubSpot form metadata
 */ export const extractPrivacyNoticeText = (hubspotData)=>{
    var _hubspotData_form_metaData;
    const legalConsentData = (_hubspotData_form_metaData = hubspotData.form.metaData) === null || _hubspotData_form_metaData === void 0 ? void 0 : _hubspotData_form_metaData.find((item)=>item.name === 'legalConsentOptions');
    if (!(legalConsentData === null || legalConsentData === void 0 ? void 0 : legalConsentData.value)) return "";
    try {
        const legalConsent = JSON.parse(legalConsentData.value);
        return legalConsent.privacyPolicyText || "";
    } catch (error) {
        Sentry.captureException(error);
        return "";
    }
};
/**
 * Extract hidden fields with default values from HubSpot form
 */ export const extractHiddenFieldsWithDefaults = (hubspotData)=>{
    const allFields = hubspotData.form.formFieldGroups.flatMap((group)=>group.fields);
    return allFields.filter((field)=>field.hidden && field.enabled && field.defaultValue).map((field)=>({
            name: field.name,
            value: field.defaultValue
        }));
};
/**
 * Parse HubSpot form data to BaseForm format
 * Main function that orchestrates the parsing process
 */ export const parseHubspotFormData = (hubspotData, localeId, formId)=>{
    var _hubspotData_form;
    if (!(hubspotData === null || hubspotData === void 0 ? void 0 : (_hubspotData_form = hubspotData.form) === null || _hubspotData_form === void 0 ? void 0 : _hubspotData_form.formFieldGroups)) {
        Sentry.captureMessage(`Invalid HubSpot form data structure with formId: ${formId}`);
        return null;
    }
    // Check if we should fallback to original HubSpot form
    if (shouldUseFallback(hubspotData, localeId, formId)) {
        return null;
    }
    // Extract hidden fields with default values
    const hiddenFieldsWithDefaults = extractHiddenFieldsWithDefaults(hubspotData);
    // Parse form fields into rows
    const rows = hubspotData.form.formFieldGroups.map((group)=>group.fields.map(parseHubspotField).filter(Boolean)).filter((row)=>row.length > 0);
    return {
        rows,
        submitButtonText: hubspotData.form.submitText,
        redirectUrl: hubspotData.form.redirectUrl,
        inlineMessage: hubspotData.form.inlineMessage,
        formType: hubspotData.form.formType,
        downloadUrl: hubspotData.form.downloadUrl,
        thankYouMessage: hubspotData.form.thankYouMessage,
        privacyNoticeText: extractPrivacyNoticeText(hubspotData),
        hiddenFieldsWithDefaults
    };
};
/**
 * Convert single field value to HubSpot format
 * Handles different value types (objects, arrays, primitives)
 */ export const convertFieldValue = (value)=>{
    if (value && typeof value === 'object' && value.value) {
        return value.value;
    }
    if (Array.isArray(value)) {
        return value.map((option)=>option.value).join(";");
    }
    return value;
};
/**
 * Convert form data to HubSpot format
 * Transforms BaseForm data structure to HubSpot API format
 */ export const convertValuesToHubspotFormat = (formData)=>{
    return formData.map((inputField)=>{
        const finalValue = convertFieldValue(inputField.value);
        if (finalValue !== null && finalValue !== undefined && finalValue !== '') {
            return {
                name: inputField.name,
                value: finalValue
            };
        }
        return null;
    }).filter(Boolean);
};
